"use client";
import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import { AuthMediaImg } from "../../../components/AuthMediaImg";
import { toMediaSrc } from "../../../utils/mediaSrc";
import { useOverlayHistory } from "../../../hooks/useOverlayHistory";
import {
  groupProductsForProductionCopy,
  getProductionProductSpecKey,
  formatProductDisplayName,
  formatProductionSerialLabel,
  specificationEntriesExcludingPlace,
  getProductPlace,
  type ProductionInchFormat,
  type ProductionCopyGroup,
  type ProductionCopySizeLine,
} from "./orderCalculations";
import { normalizeOrderLineProduct } from "./quotationSpecHelpers";

export function coerceLineProducts(raw: unknown): any[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      list = Array.isArray(parsed) ? parsed : [];
    } catch {
      list = [];
    }
  }
  return list.map((p) => normalizeOrderLineProduct(p));
}

const findColorMeta = (value: any, colorsList: any[]) => {
  if (value == null || value === "") return null;
  const name =
    typeof value === "string" || typeof value === "number"
      ? String(value)
      : String(value.name || value.Name || "");
  const idFromValue =
    typeof value === "object" && value !== null
      ? value.id ?? value.colorId ?? value.color_id
      : undefined;
  const inlineImage =
    typeof value === "object" && value !== null
      ? value.image || value.colorImage || value.color_image
      : "";
  const matched = (colorsList || []).find((c) => {
    if (idFromValue != null && String(c?.id) === String(idFromValue)) return true;
    const n = String(c?.name || "")
      .trim()
      .toLowerCase();
    return n && n === name.trim().toLowerCase();
  });
  return {
    name: name || matched?.name || "Color",
    id: idFromValue ?? matched?.id,
    image: toMediaSrc(inlineImage) || "",
    hasImage: Boolean(
      toMediaSrc(inlineImage) || matched?.hasImage || matched?.colorImage || idFromValue || matched?.id
    ),
  };
};

const isColorSpecKey = (key: string, value: any) => {
  if (/color/i.test(String(key || ""))) return true;
  if (value && typeof value === "object") {
    return Boolean(value.image || value.colorImage || value.id || value.name);
  }
  return false;
};

const renderDimValueReact = (fmt: ProductionInchFormat) => {
  if (fmt.isEmpty) return "N/A";
  return (
    <>
      {fmt.displayInches}
      {fmt.noolCount > 0 ? (
        <sup className="text-xs">
          {fmt.noolSign}
          {fmt.noolCount}
        </sup>
      ) : null}
    </>
  );
};

const formatViewDetailsUom = (uom?: string | null) => {
  const u = String(uom || "").trim();
  if (!u) return "inch";
  const lower = u.toLowerCase();
  if (lower === "in" || lower === "inch" || lower === "inches") return "inch";
  if (lower === "ft" || lower === "feet" || lower === "foot") return "Feet";
  if (lower === "m" || lower === "meter" || lower === "metre" || lower === "meters") return "m";
  if (lower === "cm") return "cm";
  if (lower === "mm") return "mm";
  return u;
};

const hasMeaningfulRawDim = (
  raw: string | undefined,
  nool: number | string | undefined
) => {
  const noolNum = Number(nool);
  const hasNool = Number.isFinite(noolNum) && noolNum !== 0;
  if (hasNool) return true;
  if (raw == null) return false;
  const t = String(raw).trim();
  if (!t) return false;
  const n = Number(t);
  if (Number.isFinite(n) && n === 0) return false;
  return true;
};

const renderRawDimReact = (
  raw: string | undefined,
  nool: number | string | undefined,
  uom: string,
  valueClassName: string
) => {
  if (!hasMeaningfulRawDim(raw, nool)) return null;
  const noolNum = Number(nool);
  const hasNool = Number.isFinite(noolNum) && noolNum !== 0;
  const label = formatViewDetailsUom(uom);
  return (
    <span className={valueClassName}>
      {raw != null && String(raw).trim() !== "" ? String(raw) : "0"}
      {hasNool ? (
        <sup className="text-xs">
          {noolNum > 0 ? "+" : ""}
          {noolNum}
        </sup>
      ) : null}{" "}
      ({label})
    </span>
  );
};

const renderViewDimReact = (
  fmt: ProductionInchFormat,
  raw: string | undefined,
  nool: number | string | undefined,
  uom: string,
  valueClassName: string
) => {
  const isInch = formatViewDetailsUom(uom) === "inch";
  if (isInch && !fmt.isEmpty) {
    return (
      <span className={valueClassName}>
        {renderDimValueReact(fmt)} ({formatViewDetailsUom(uom)})
      </span>
    );
  }
  return (
    renderRawDimReact(raw, nool, uom, valueClassName) ||
    (!fmt.isEmpty ? (
      <span className={valueClassName}>
        {renderDimValueReact(fmt)} ({formatViewDetailsUom(uom)})
      </span>
    ) : null)
  );
};

const renderSizeLineReact = (size: ProductionCopySizeLine) => {
  const qty = size.quantity > 0 ? size.quantity : "N/A";
  const whUom = size.whUom || "inch";
  const lengthUom = size.lengthUom || whUom || "inch";
  const formula = String(size.formulaType || "")
    .trim()
    .toLowerCase();

  const hasW =
    hasMeaningfulRawDim(size.rawWidth, size.noolWidth) || !size.width.isEmpty;
  const hasH =
    hasMeaningfulRawDim(size.rawHeight, size.noolHeight) || !size.height.isEmpty;
  const hasL =
    hasMeaningfulRawDim(size.rawLength, size.noolLength) || !size.length.isEmpty;

  const isLengthFormula = formula === "length";
  const isPiecesFormula = formula === "pieces";
  const lengthOnly = isLengthFormula || (hasL && !hasW && !hasH);

  if (isPiecesFormula && !hasW && !hasH && !hasL) {
    return (
      <>
        Qty : <span className="text-red-700">{qty}</span>
      </>
    );
  }

  if (lengthOnly) {
    const lengthNode = renderViewDimReact(
      size.length,
      size.rawLength,
      size.noolLength,
      lengthUom,
      "text-cyan-700"
    );
    return (
      <>
        {lengthNode ? <>Length : {lengthNode} </> : null}
        Qty : <span className="text-red-700">{qty}</span>
      </>
    );
  }

  const bits: ReactNode[] = [];
  const wNode = renderViewDimReact(
    size.width,
    size.rawWidth,
    size.noolWidth,
    whUom,
    "text-blue-700"
  );
  if (wNode) {
    bits.push(
      <span key="w">
        W : {wNode}
      </span>
    );
  }
  const hNode = renderViewDimReact(
    size.height,
    size.rawHeight,
    size.noolHeight,
    whUom,
    "text-cyan-700"
  );
  if (hNode) {
    bits.push(
      <span key="h">
        H : {hNode}
      </span>
    );
  }

  if (bits.length === 0) {
    return (
      <>
        Qty : <span className="text-red-700">{qty}</span>
      </>
    );
  }
  return (
    <>
      {bits.map((bit, i) => (
        <span key={i}>
          {i > 0 ? " * " : null}
          {bit}
        </span>
      ))}{" "}
      Qty : <span className="text-red-700">{qty}</span>
    </>
  );
};

const totalQtyForGroup = (group: ProductionCopyGroup) =>
  (group.sizes || []).reduce((sum, size) => sum + (Number(size.quantity) || 0), 0);

type Props = {
  products: unknown;
  showMoney: boolean;
  accountId: string;
  authToken: string;
  historyKeyPrefix: string;
};

export const OrderProductsDetailsSection: React.FC<Props> = ({
  products,
  showMoney,
  accountId,
  authToken,
  historyKeyPrefix,
}) => {
  const lineItems = useMemo(() => coerceLineProducts(products), [products]);
  const [colorsList, setColorsList] = useState<any[]>([]);
  const [previewColor, setPreviewColor] = useState<any>(null);
  const [previewProduct, setPreviewProduct] = useState<any>(null);

  useOverlayHistory(!!previewColor, () => setPreviewColor(null), `${historyKeyPrefix}-color`);
  useOverlayHistory(!!previewProduct, () => setPreviewProduct(null), `${historyKeyPrefix}-product`);

  useEffect(() => {
    if (!lineItems.length || !accountId || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { fetchColorsCached } = await import("../../../utils/colorsCache");
        const { colors } = await fetchColorsCached({
          accountId,
          authToken,
          lite: true,
          maxResults: "all",
        });
        if (!cancelled) setColorsList(Array.isArray(colors) ? colors : []);
      } catch {
        if (!cancelled) setColorsList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lineItems.length, accountId, authToken]);

  if (!lineItems.length) return null;

  return (
    <>
      <div className="border-t pt-3">
        <h3 className="text-lg font-bold text-gray-900 mb-4">🛍️ Products</h3>
        <div className="space-y-4">
          {groupProductsForProductionCopy(lineItems).map((group, index) => {
            const matchingProducts = lineItems.filter(
              (p: any) => getProductionProductSpecKey(p) === group.specKey
            );
            const firstProduct = matchingProducts[0] as any;
            const isPieces = firstProduct?.formulaType === "pieces";
            const totalPrice = matchingProducts.reduce(
              (sum: number, p: any) => sum + (p.price ? parseFloat(p.price.toString()) : 0),
              0
            );
            const totalArea = matchingProducts.reduce(
              (sum: number, p: any) => sum + (p.area ? parseFloat(p.area.toString()) : 0),
              0
            );

            return (
              <div
                key={index}
                className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 space-y-3"
              >
                <div className="bg-white rounded-lg p-3 border border-green-300">
                  <p className="text-xs text-gray-600 font-bold uppercase mb-2">Product Name</p>
                  <div className="flex items-center gap-3">
                    <AuthMediaImg
                      kind="product"
                      resourceId={firstProduct?.product_id || firstProduct?.productId}
                      accountId={accountId}
                      authToken={authToken}
                      inlineSrc={firstProduct?.productImage || firstProduct?.product_image}
                      hasImage={Boolean(
                        firstProduct?.product_id ||
                          firstProduct?.productId ||
                          firstProduct?.productImage ||
                          firstProduct?.hasImage
                      )}
                      alt={group.name}
                      className="w-14 h-14 rounded-lg border border-green-300 object-cover flex-shrink-0 cursor-pointer hover:shadow-md hover:scale-105 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewProduct({
                          id: firstProduct?.product_id || firstProduct?.productId,
                          name: group.name,
                          productImage:
                            firstProduct?.productImage || firstProduct?.product_image,
                          hasImage: true,
                        });
                      }}
                      fallback={
                        <div className="w-14 h-14 rounded-lg border border-green-300 bg-gray-100 flex items-center justify-center flex-shrink-0 text-lg">
                          📦
                        </div>
                      }
                    />
                    <p className="text-lg font-bold text-green-700 min-w-0 break-words">
                      {formatProductDisplayName(group.name, getProductPlace(firstProduct))}
                    </p>
                  </div>
                </div>

                {(() => {
                  const entries = specificationEntriesExcludingPlace(
                    firstProduct?.specifications,
                    firstProduct?.specOrder
                  );
                  if (entries.length === 0) return null;
                  return (
                    <div className="bg-white border-2 border-yellow-300 rounded-lg p-4">
                      <p className="text-xs text-gray-600 font-bold uppercase mb-3">Specifications</p>
                      {(() => {
                        const rows: Array<Array<[string, any]>> = [];
                        for (let i = 0; i < entries.length; i += 3) {
                          rows.push(entries.slice(i, i + 3) as Array<[string, any]>);
                        }
                        const renderSpecValue = (key: string, value: any) => {
                          const colorMeta =
                            isColorSpecKey(key, value) ||
                            (typeof value === "object" && value !== null && (value.image || value.name))
                              ? findColorMeta(value, colorsList)
                              : null;
                          if (colorMeta && (colorMeta.hasImage || colorMeta.name)) {
                            return (
                              <div className="flex items-center gap-2 min-w-0">
                                <AuthMediaImg
                                  kind="color"
                                  resourceId={colorMeta.id}
                                  accountId={accountId}
                                  authToken={authToken}
                                  inlineSrc={colorMeta.image}
                                  hasImage={colorMeta.hasImage}
                                  alt={colorMeta.name}
                                  className="w-8 h-8 rounded border border-yellow-300 object-cover cursor-pointer hover:shadow-lg hover:scale-110 transition-all shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewColor(colorMeta);
                                  }}
                                  fallback={
                                    <div className="w-8 h-8 rounded border border-yellow-300 bg-gray-200 shrink-0" />
                                  }
                                />
                                <p className="text-sm font-bold text-yellow-700 break-words">
                                  {colorMeta.name || "N/A"}
                                </p>
                              </div>
                            );
                          }
                          if (typeof value === "object" && value !== null && value.name) {
                            return (
                              <p className="text-sm font-bold text-yellow-700 break-words">
                                {value.name || "N/A"}
                              </p>
                            );
                          }
                          return (
                            <p className="text-sm font-bold text-yellow-700 break-words">
                              {value || "N/A"}
                            </p>
                          );
                        };
                        return (
                          <div className="space-y-4">
                            {rows.map((row, rowIdx) => (
                              <div
                                key={`detail-spec-row-${rowIdx}`}
                                className="grid gap-4"
                                style={{
                                  gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                                }}
                              >
                                {row.map(([key, value]) => (
                                  <div key={key}>
                                    <p className="text-xs text-gray-600 font-bold capitalize mb-1">
                                      {key.replace(/_/g, " ")}
                                    </p>
                                    {renderSpecValue(key, value)}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                <div className="bg-white border-2 border-blue-300 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-600 font-bold mb-1">Place</p>
                    <p className="text-sm font-bold text-teal-700 break-words">
                      {getProductPlace(firstProduct) || "N/A"}
                    </p>
                  </div>

                  <div className="space-y-2 border-t border-blue-100 pt-3">
                    {group.sizes.map((size, sizeIdx) => (
                      <div key={sizeIdx} className="text-sm font-bold text-gray-800 break-words">
                        <span className="text-blue-800">
                          {formatProductionSerialLabel(size.serialNumbers)} =
                        </span>{" "}
                        {renderSizeLineReact(size)}
                      </div>
                    ))}
                    <div className="text-sm font-bold text-gray-900 pt-1 border-t border-blue-100">
                      Total qty : {totalQtyForGroup(group)}
                    </div>
                  </div>

                  {showMoney && !isPieces && (
                    <div className="grid grid-cols-2 gap-4 border-t border-blue-100 pt-3">
                      {firstProduct && (
                        <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Area/Unit</p>
                          <p className="text-sm font-bold text-purple-700 break-words">
                            {firstProduct.area_per_unit
                              ? parseFloat(firstProduct.area_per_unit.toString()).toFixed(2)
                              : "N/A"}{" "}
                            {firstProduct.area_uom || ""}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-600 font-bold mb-1">Area</p>
                        <p className="text-sm font-bold text-indigo-700 break-words">
                          {totalArea > 0 ? totalArea.toFixed(2) : "N/A"}{" "}
                          {firstProduct?.area_uom || ""}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {showMoney && (
                  <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <p className="text-xs text-gray-600 font-bold mb-1">Price</p>
                        <p className="text-sm font-bold text-pink-700 break-words">
                          ₹{totalPrice.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-xs text-gray-600 font-bold mb-1">GST</p>
                        <p className="text-sm font-bold text-orange-700 break-words">
                          {firstProduct?.gst || "0"}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {previewColor && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4"
          onClick={() => setPreviewColor(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewColor(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-pink-300 shadow-md flex items-center justify-center bg-gray-50">
              <AuthMediaImg
                kind="color"
                resourceId={previewColor.id}
                accountId={accountId}
                authToken={authToken}
                inlineSrc={previewColor.image || previewColor.colorImage}
                hasImage={Boolean(
                  previewColor.hasImage ||
                    previewColor.id ||
                    previewColor.image ||
                    previewColor.colorImage
                )}
                alt={previewColor.name}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                }
              />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewColor.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Color</p>
            </div>
          </div>
        </div>
      )}

      {previewProduct && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4"
          onClick={() => setPreviewProduct(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewProduct(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-emerald-300 shadow-md flex items-center justify-center bg-gray-50">
              <AuthMediaImg
                kind="product"
                resourceId={previewProduct.id}
                accountId={accountId}
                authToken={authToken}
                inlineSrc={previewProduct.productImage}
                hasImage={Boolean(
                  previewProduct.hasImage || previewProduct.id || previewProduct.productImage
                )}
                alt={previewProduct.name}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                }
              />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewProduct.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Product</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
